import { useContext, useEffect, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Slider from '@mui/material/Slider';
import { UMInputNumber } from '@urbanmetrix/um-kit';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import { GeodataContext } from 'contexts/GeodataContext';
import { getInputNumberRangeErrorMessage } from 'utils/inputNumberErrorMessage';
import {
  isConcaveAlphaValueValid,
  isTreesDonutRadiusValid,
  isTreesMinimalHeightValid,
  isTreesRadiusValid,
} from './geodataNumericValidation';

interface TreesProps {
  readOnly?: boolean;
  storageImportDisabled?: boolean;
  hideIncludeToggle?: boolean;
}

const accuracyBySliderValue: Record<
  number,
  { cecimationCellEdge: number; chipperCapacity: number; minPoints: number }
> = {
  0: { cecimationCellEdge: 1.5, chipperCapacity: 5000, minPoints: 50 },
  50: { cecimationCellEdge: 0.75, chipperCapacity: 7500, minPoints: 100 },
  100: { cecimationCellEdge: 0, chipperCapacity: 10000, minPoints: 150 },
};

const accuracyMarks = [
  { value: 0, label: 'Low' },
  { value: 50, label: 'Middle' },
  { value: 100, label: 'High' },
];

const Trees = ({ readOnly = false, hideIncludeToggle = false }: TreesProps) => {
  const { geodata, geodataParams, setGeodata, setGeodataParams } = useContext(GeodataContext);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const includeTrees = Boolean(geodata?.treesEnabled);
  const isTreesExtensionValid = !includeTrees || isTreesRadiusValid(geodataParams?.treesRadius);
  const isTreesInternalRadiusValid = !includeTrees || isTreesDonutRadiusValid(geodataParams?.treesDonutRadius);
  const isTreesMinimumHeightValid = !includeTrees || isTreesMinimalHeightValid(geodataParams?.treesMinimalHeight);
  const isConcaveValueValid = !includeTrees || isConcaveAlphaValueValid(geodataParams?.concaveAlphaValue);
  const hasAdvancedTreesOptionsError =
    !isTreesInternalRadiusValid ||
    !isTreesMinimumHeightValid ||
    (geodataParams?.concaveAlphaValue != null && !isConcaveValueValid);

  const resolveSliderValue = () => {
    if (
      Number(geodataParams?.cecimationCellEdge) === 1.5 &&
      Number(geodataParams?.chipperCapacity) === 5000 &&
      Number(geodataParams?.minPoints) === 50
    ) {
      return 0;
    }
    if (
      Number(geodataParams?.cecimationCellEdge) === 0 &&
      Number(geodataParams?.chipperCapacity) === 10000 &&
      Number(geodataParams?.minPoints) === 150
    ) {
      return 100;
    }
    return 50;
  };
  const [accuracyValue, setAccuracyValue] = useState(resolveSliderValue());

  const handleGeodataInputChange = (name: string | undefined, value: number | string) => {
    setGeodataParams({ [String(name)]: value });
  };

  useEffect(() => {
    setAccuracyValue(resolveSliderValue());
  }, [geodataParams?.cecimationCellEdge, geodataParams?.chipperCapacity, geodataParams?.minPoints]);

  return (
    <div className="geodata-section-block">
      {!hideIncludeToggle && (
        <FormControlLabel
          className="checkbox-row"
          control={
            <Checkbox
              checked={includeTrees}
              disabled={readOnly}
              onChange={(event) =>
                setGeodata((prev) => ({
                  ...prev,
                  treesEnabled: event.target.checked,
                }))
              }
            />
          }
          label="Include Trees"
        />
      )}

      <FormControlLabel
        className="checkbox-row"
        control={
          <Checkbox
            checked={Boolean(geodataParams?.treesInAnalysisArea)}
            disabled={readOnly || !includeTrees}
            onChange={(event) =>
              setGeodataParams({
                treesInAnalysisArea: event.target.checked,
              })
            }
          />
        }
        label="Trees inside Analysis Area"
      />

      <div className="geodata-inline-number" style={{ marginTop: '10px' }}>
        <UMInputNumber
          label={
            <span
              className="geodata-slider-field__label geodata-slider-field__label--with-info"
              style={{ marginBottom: '2px' }}
            >
              <span>Trees Extension [m]</span>
              <CustomTooltip title="Trees outside this area will not be considered." arrow />
            </span>
          }
          name="treesRadius"
          value={geodataParams?.treesRadius}
          width={84}
          maxErrorMessageWidth={252}
          onChange={handleGeodataInputChange}
          defaultValue={200}
          min={50}
          max={1500}
          errorMessage={getInputNumberRangeErrorMessage(50, 1500)}
          valid={isTreesExtensionValid}
          disabled={readOnly || !includeTrees}
        />
      </div>

      <button
        type="button"
        style={{ marginTop: '18px', marginBottom: ' 8px' }}
        className={`geodata-subaccordion__trigger geodata-subaccordion__trigger--nested${
          hasAdvancedTreesOptionsError ? ' geodata-subaccordion__trigger--error' : ''
        }`}
        onClick={() => setShowAdvancedOptions((prev) => !prev)}
      >
        <span>{showAdvancedOptions ? '▾' : '▸'}</span>
        <span>Advanced Trees options</span>
      </button>

      {showAdvancedOptions && (
        <div className="geodata-subaccordion__content geodata-subaccordion__content--dense">
          <div className="geodata-inline-number">
            <UMInputNumber
              label={
                <span
                  className="geodata-slider-field__label geodata-slider-field__label--with-info"
                  style={{ whiteSpace: 'nowrap', marginBottom: '2px' }}
                >
                  <span>Trees internal radius [m]</span>
                  <CustomTooltip title="Trees inside this area will not be considered" arrow />
                </span>
              }
              name="treesDonutRadius"
              value={geodataParams?.treesDonutRadius}
              width={84}
              maxErrorMessageWidth={252}
              onChange={handleGeodataInputChange}
              defaultValue={0}
              min={0}
              max={5000}
              errorMessage={getInputNumberRangeErrorMessage(0, 5000)}
              valid={isTreesInternalRadiusValid}
              disabled={readOnly || !includeTrees}
            />
          </div>

          <div className="geodata-inline-number">
            <UMInputNumber
              label={
                <span
                  className="geodata-slider-field__label geodata-slider-field__label--with-info"
                  style={{ whiteSpace: 'nowrap', marginBottom: '2px' }}
                >
                  <span>Trees minimum height [m]</span>
                  <CustomTooltip title="Small trees under this height will not be considered." arrow />
                </span>
              }
              name="treesMinimalHeight"
              value={geodataParams?.treesMinimalHeight}
              width={84}
              style={{ marginBottom: '6px' }}
              maxErrorMessageWidth={252}
              onChange={handleGeodataInputChange}
              defaultValue={3}
              min={1}
              max={50}
              errorMessage={getInputNumberRangeErrorMessage(1, 50)}
              valid={isTreesMinimumHeightValid}
              disabled={readOnly || !includeTrees}
            />
          </div>

          <div className="geodata-slider-field">
            <div className="geodata-slider-field__label geodata-slider-field__label--with-info">
              <span>Trees Accuracy</span>
              <CustomTooltip title="Lower 3D geometrical accuracy corresponds to faster processing." arrow />
            </div>
            <Slider
              disabled={readOnly || !includeTrees}
              min={0}
              max={100}
              step={50}
              marks={accuracyMarks}
              value={accuracyValue}
              onChange={(_, value) => {
                const nextValue = Array.isArray(value) ? value[0] : value;
                setAccuracyValue(nextValue);
              }}
              onChangeCommitted={(_, value) => {
                const nextValue = Array.isArray(value) ? value[0] : value;
                setGeodataParams(accuracyBySliderValue[nextValue] ?? accuracyBySliderValue[50]);
              }}
            />
          </div>

          <FormControlLabel
            className="checkbox-row"
            control={
              <Checkbox
                checked={geodataParams?.concaveAlphaValue != null}
                disabled={readOnly || !includeTrees}
                onChange={(event) =>
                  setGeodataParams({
                    concaveAlphaValue: event.target.checked ? Number(geodataParams?.concaveAlphaValue ?? 2) : null,
                  })
                }
              />
            }
            label="Use concave triangulation"
          />

          {geodataParams?.concaveAlphaValue != null && (
            <div className="geodata-inline-number geodata-inline-number--tight">
              <UMInputNumber
                label=""
                name="concaveAlphaValue"
                value={geodataParams?.concaveAlphaValue}
                width={84}
                maxErrorMessageWidth={252}
                onChange={handleGeodataInputChange}
                defaultValue={2}
                min={0}
                max={100}
                errorMessage={getInputNumberRangeErrorMessage(0, 100)}
                valid={isConcaveValueValid}
                disabled={readOnly || !includeTrees}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Trees;
